import { t as uiText } from '../i18n';
// src/components/FileStatsDisplay.tsx
import React from 'react';
import { useParams } from 'react-router-dom';
import { useWorkspaceContext } from '../context/WorkspaceContext';
import { formatFileSize } from '../features/workspace/fileStatsUtils';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { getEntitlements } from '../features/plans/entitlements';

interface FileStatsDisplayProps {
  showVerifyButton?: boolean;
  className?: string;
  refreshTrigger?: number;
}

const FileStatsDisplay: React.FC<FileStatsDisplayProps> = ({ 
  showVerifyButton = false, 
  className = '',
  refreshTrigger = 0
}) => {
  const { workspaceId: routeWorkspaceId } = useParams();
  const { fileStats, fileStatsLoading, refreshFileStats, verifyFileStats } = useWorkspaceContext();
  const [workspaceOfferId, setWorkspaceOfferId] = React.useState<string | null>(null);
  const [workspacePlanId, setWorkspacePlanId] = React.useState<string>('free');
  const [workspaceSeriesId, setWorkspaceSeriesId] = React.useState<string>('');
  const [parentOffer, setParentOffer] = React.useState<{
    parentWorkspaceId: string;
    offerId?: string;
    planId?: string;
    seriesId?: string;
  } | null>(null);

  // ワークスペースのプラン/オファー情報を取得（表示用）
  React.useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!routeWorkspaceId) {
        if (!cancelled) {
          setWorkspaceOfferId(null);
          setWorkspacePlanId('free');
          setWorkspaceSeriesId('');
          setParentOffer(null);
        }
        return;
      }
      try {
        const snap = await getDoc(doc(db, 'workspaces', routeWorkspaceId));
        if (!snap.exists()) {
          if (!cancelled) {
            setWorkspaceOfferId(null);
            setWorkspacePlanId('free');
            setWorkspaceSeriesId('');
            setParentOffer(null);
          }
          return;
        }
        const data = snap.data() as any;
        const offerId = typeof data?.offerId === 'string' ? data.offerId : null;
        const planIdRaw = typeof data?.planId === 'string' ? data.planId : '';
        const planId = planIdRaw.trim() ? planIdRaw.trim() : 'free';
        const seriesId = typeof data?.seriesId === 'string' ? data.seriesId.trim() : '';
        const parentId = typeof data?.parentWorkspaceId === 'string' ? data.parentWorkspaceId.trim() : '';
        let parent: typeof parentOffer = null;
        if (parentId) {
          parent = { parentWorkspaceId: parentId };
          try {
            const parentSnap = await getDoc(doc(db, 'workspaces', parentId));
            const p = parentSnap.data() as any;
            parent = {
              parentWorkspaceId: parentId,
              offerId: typeof p?.offerId === 'string' ? p.offerId : undefined,
              planId: typeof p?.planId === 'string' ? p.planId : undefined,
              seriesId: typeof p?.seriesId === 'string' ? p.seriesId : undefined,
            };
          } catch {
            parent = { parentWorkspaceId: parentId };
          }
        }
        if (!cancelled) {
          setWorkspaceOfferId(offerId);
          setWorkspacePlanId(planId);
          setWorkspaceSeriesId(seriesId);
          setParentOffer(parent);
        }
      } catch (e) {
        console.error('[FileStatsDisplay] Failed to load workspace offer/plan:', e);
        if (!cancelled) {
          setWorkspaceOfferId(null);
          setWorkspacePlanId('free');
          setWorkspaceSeriesId('');
          setParentOffer(null);
        }
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [routeWorkspaceId]);

  // refreshTriggerが変更されたら統計を更新
  React.useEffect(() => {
    if (refreshTrigger > 0) {
      refreshFileStats();
    }
  }, [refreshTrigger, refreshFileStats]);

  const handleVerifyStats = async () => {
    try {
      const result = await verifyFileStats();
      if (result.wasFixed) {
        alert(uiText("✅ Updated file stats\n\nBefore: {{v0}}\nAfter: {{v1}}", { v0: formatFileSize(result.beforeStats?.totalFileSize || 0), v1: formatFileSize(result.afterStats.totalFileSize) }));
      } else {
        alert(uiText("✅ No issues found in file stats"));
      }
    } catch (error) {
      console.error('ファイル統計の検証エラー:', error);
      alert(uiText("❌ An error occurred while verifying file stats"));
    }
  };

  if (fileStatsLoading) {
    return (
      <div className={`file-stats-display ${className}`} style={{ padding: '12px', background: '#f9f9f9', borderRadius: '8px', fontSize: '14px' }}>
        <div style={{ color: '#666' }}>{uiText("Loading file statistics...")}</div>
      </div>
    );
  }

  if (!fileStats) {
    return (
      <div className={`file-stats-display ${className}`} style={{ padding: '12px', background: '#f9f9f9', borderRadius: '8px', fontSize: '14px' }}>
        <div style={{ color: '#999' }}>{uiText("File statistics are unavailable")}</div>
      </div>
    );
  }

  // デフォルトは暫定的に 100MB。exclusive は 1TB。
  const entitlements = getEntitlements({
    offerId: workspaceOfferId,
    planId: workspacePlanId,
    seriesId: workspaceSeriesId,
    parentWorkspaceId: parentOffer?.parentWorkspaceId,
    parent: parentOffer
      ? { offerId: parentOffer.offerId, planId: parentOffer.planId, seriesId: parentOffer.seriesId }
      : undefined,
  });
  const limitBytes = entitlements.storageLimitBytes ?? (1024 * 1024 * 1024 * 1024); // 万一nullなら表示上は1TB

  const usagePercentage = Math.round((fileStats.totalFileSize / limitBytes) * 100);

  return (
    <div className={`file-stats-display ${className}`} style={{ padding: '12px', background: '#f9f9f9', borderRadius: '8px', fontSize: '14px' }}>
      {/* タイトルと最終更新日時を横並びで表示 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <div style={{ fontWeight: '600', color: '#333' }}>{uiText("File usage")}</div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {uiText("Current plan:")}<strong style={{ color: '#333' }}>
              {parentOffer
                ? uiText("{{v0}} (inherits parent plan)", { v0: (workspacePlanId === 'child' ? (parentOffer.planId || 'free') : workspacePlanId) || 'free' })
                : (workspacePlanId || 'free')}
            </strong>
          </div>
          {fileStats.lastFileSizeUpdate && (
            <div style={{ fontSize: '12px', color: '#999' }}>
              {uiText("Last updated:")} {fileStats.lastFileSizeUpdate.toLocaleString()}
            </div>
          )}
        </div>
      </div>
      
      {/* 合計サイズとファイル数を横並びで表示 */}
      <div style={{ display: 'flex', gap: '24px', marginBottom: '4px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ color: '#666' }}>{uiText("Total size:")}</span>
          <span style={{ fontWeight: '500', color: usagePercentage > 80 ? '#d32f2f' : '#333' }}>
            {formatFileSize(fileStats.totalFileSize)}
          </span>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ color: '#666' }}>{uiText("File count:")}</span>
          <span style={{ fontWeight: '500' }}>{fileStats.fileCount}{uiText(" units")}</span>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
        <span style={{ color: '#666' }}>{uiText("Usage:")}</span>
        <span style={{ fontWeight: '500', color: usagePercentage > 80 ? '#d32f2f' : '#333' }}>
          {usagePercentage}% ({formatFileSize(limitBytes)}{uiText(" limit)")}
        </span>
      </div>

      {/* 使用率バー */}
      <div style={{ width: '100%', height: '6px', background: '#e0e0e0', borderRadius: '3px', marginBottom: '8px', overflow: 'hidden' }}>
        <div 
          style={{ 
            width: `${Math.min(usagePercentage, 100)}%`, 
            height: '100%', 
            background: usagePercentage > 80 ? '#d32f2f' : usagePercentage > 60 ? '#ff9800' : 'var(--workspace-primary, #cfae91)',
            transition: 'width 0.3s ease'
          }} 
        />
      </div>

      {usagePercentage > 80 && (
        <div style={{ color: '#d32f2f', fontSize: '12px', marginBottom: '8px' }}>
          {uiText("⚠️ Storage usage is nearing the limit")}
        </div>
      )}



      {showVerifyButton && (
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={refreshFileStats}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              background: '#e3f2fd',
              color: '#1976d2',
              border: '1px solid #1976d2',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {uiText("🔄 Refresh")}
          </button>
          <button
            onClick={handleVerifyStats}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              background: '#fff3e0',
              color: '#f57c00',
              border: '1px solid #f57c00',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {uiText("🔍 Verify")}
          </button>
        </div>
      )}
    </div>
  );
};

export default FileStatsDisplay;
